/* eslint-disable indent */
"use client";

import { useCallback, useEffect, useState } from "react";
import { ColorResult, SketchPicker } from "react-color";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import debounce from "lodash/debounce";
import { Code, List as ListIcon, Quote } from "lucide-react"; // Import necessary icons
import { Bold, Copy, Dice5, Eye, EyeOff, Heading1, Heading2, Image as ImageIcon, Italic, Link as LinkIcon, MoreHorizontal, Palette, Redo, Tag, Underline as UnderlineIcon, Undo } from "lucide-react";
import InsertDialog from "@/components/atoms/InsertDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BubbleMenuButton, Card, CardContent, CardFooter, EditorContainer, LastSavedText, LoadingContainer, SaveButton, Spinner, StyledButton, TagButton, Toolbar, WordCountText } from "./Note.styled";
type NotesProps = {
  initialContent?: string;
  onSave: (content: string) => void;
  onClose: () => void;
};
export default function Notes({
  initialContent = "",
  onSave,
  onClose
}: NotesProps) {
  const [content, setContent] = useState(initialContent);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [diceRoll, setDiceRoll] = useState<number | null>(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customColor, setCustomColor] = useState("");
  const [allTags, setAllTags] = useState<string[]>([]);
  const [isEditable, setIsEditable] = useState(true);
  const editor = useEditor({
    extensions: [StarterKit, Image, Link.configure({
      openOnClick: false
    }), Highlight.configure({
      multicolor: true
    }), Underline, TextStyle, Color],
    editable: isEditable,
    content: content,
    onUpdate: ({
      editor
    }) => {
      const newContent = editor.getHTML();
      setContent(newContent);
      const text = editor.state.doc.textContent;
      setWordCount(text.trim().split(/\s+/).length);
      setCharCount(text.length);
      debouncedSave(newContent);
    },
    onCreate: ({
      editor
    }) => {
      setIsLoading(false);
      const text = editor.state.doc.textContent;
      setWordCount(text.trim().split(/\s+/).length);
      setCharCount(text.length);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl focus:outline-none",
        placeholder: "Start typing your notes here...",
        style: "color: #ffffff;"
      }
    },
    immediatelyRender: false
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(debounce(async (newContent: string) => {
    setIsSaving(true);
    await onSave(newContent);
    setLastSaved(new Date());
    setIsSaving(false);
  }, 2000), [onSave]);
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditable);
    }
  }, [isEditable, editor]);
  useEffect(() => {
    if (editor && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent);
    }
  }, [editor, initialContent]);

  // Clean up the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);
  const toggleCommandMap: Record<string, () => void> = {
    bold: () => editor?.chain().focus().toggleBold().run(),
    italic: () => editor?.chain().focus().toggleItalic().run(),
    underline: () => editor?.chain().focus().toggleUnderline().run()
  };
  const handleSave = () => {
    onSave(content);
    setLastSaved(new Date());
    onClose();
  };
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(editor?.getHTML() || "");
  }, [editor]);
  const handleAddTag = useCallback(() => {
    const tag = prompt("Enter a tag:");
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      if (!allTags.includes(tag)) {
        setAllTags([...allTags, tag]);
      }
    }
  }, [tags, allTags]);
  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  }, [tags]);
  const focusEditor = useCallback(() => {
    if (editor) {
      editor.commands.focus();
    }
  }, [editor]);
  const handleDiceRoll = useCallback(() => {
    setDiceRoll(Math.floor(Math.random() * 20) + 1);
  }, []);
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case "b":
          event.preventDefault();
          editor?.chain().focus().toggleBold().run();
          break;
        case "i":
          event.preventDefault();
          editor?.chain().focus().toggleItalic().run();
          break;
        case "u":
          event.preventDefault();
          editor?.chain().focus().toggleUnderline().run();
          break;
        default:
          break;
      }
    }
  }, [editor]);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
  const handleTagSuggestionClick = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };
  if (isLoading) {
    return <Card>
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            </Card>;
  }
  if (!editor) {
    return null;
  }
  return <>
            <Card onClick={focusEditor} data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <CardContent className="rounded-lg !w-full" data-sentry-element="CardContent" data-sentry-source-file="index.tsx">
                    <Toolbar data-sentry-element="Toolbar" data-sentry-source-file="index.tsx">
                        <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Bold className="h-4 w-4" data-sentry-element="Bold" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Bold</TooltipContent>
                            </Tooltip>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Italic className="h-4 w-4" data-sentry-element="Italic" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Italic</TooltipContent>
                            </Tooltip>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <UnderlineIcon className="h-4 w-4" data-sentry-element="UnderlineIcon" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Underline</TooltipContent>
                            </Tooltip>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleHeading({
                  level: 1
                }).run()} active={editor.isActive("heading", {
                  level: 1
                })} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Heading1 className="h-4 w-4" data-sentry-element="Heading1" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Heading 1</TooltipContent>
                            </Tooltip>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleHeading({
                  level: 2
                }).run()} active={editor.isActive("heading", {
                  level: 2
                })} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Heading2 className="h-4 w-4" data-sentry-element="Heading2" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Heading 2</TooltipContent>
                            </Tooltip>
                            <div className="flex gap-1">
                                <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                    <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                        <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().undo().run()} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                            <Undo className="h-4 w-4" data-sentry-element="Undo" data-sentry-source-file="index.tsx" />
                                        </StyledButton>
                                    </TooltipTrigger>
                                    <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Undo</TooltipContent>
                                </Tooltip>
                                <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                    <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                        <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().redo().run()} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                            <Redo className="h-4 w-4" data-sentry-element="Redo" data-sentry-source-file="index.tsx" />
                                        </StyledButton>
                                    </TooltipTrigger>
                                    <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Redo</TooltipContent>
                                </Tooltip>
                            </div>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <ListIcon className="h-4 w-4" data-sentry-element="ListIcon" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Bullet List</TooltipContent>
                            </Tooltip>

                            {/* Ordered List */}
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <ListIcon className="h-4 w-4" data-sentry-element="ListIcon" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Ordered List</TooltipContent>
                            </Tooltip>

                            {/* Blockquote */}
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Quote className="h-4 w-4" data-sentry-element="Quote" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Blockquote</TooltipContent>
                            </Tooltip>
                            {/* Code Block */}
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Code className="h-4 w-4" data-sentry-element="Code" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Code Block</TooltipContent>
                            </Tooltip>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="index.tsx">
                                        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="index.tsx">
                                            <StyledButton variant="outline" size="icon" data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                                <Palette className="h-4 w-4" data-sentry-element="Palette" data-sentry-source-file="index.tsx" />
                                            </StyledButton>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" sideOffset={5} className="p-0" data-sentry-element="DropdownMenuContent" data-sentry-source-file="index.tsx">
                                            <div className="p-2">
                                                <SketchPicker color={customColor} onChangeComplete={(color: ColorResult) => {
                        setCustomColor(color.hex);
                        editor.chain().focus().setColor(color.hex).run();
                      }} data-sentry-element="SketchPicker" data-sentry-source-file="index.tsx" />
                                            </div>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Text Color</TooltipContent>
                            </Tooltip>
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" onClick={() => setIsEditable(!isEditable)} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        {isEditable ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                    </StyledButton>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                    {isEditable ? "Switch to Read-Only" : "Switch to Edit"}
                                </TooltipContent>
                            </Tooltip>
                            <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="index.tsx">
                                <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <MoreHorizontal className="h-4 w-4" data-sentry-element="MoreHorizontal" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent data-sentry-element="DropdownMenuContent" data-sentry-source-file="index.tsx">
                                    <DropdownMenuItem onSelect={() => setIsImageDialogOpen(true)} data-sentry-element="DropdownMenuItem" data-sentry-source-file="index.tsx">
                                        <ImageIcon className="mr-2 h-4 w-4" data-sentry-element="ImageIcon" data-sentry-source-file="index.tsx" />
                                        <span>Insert Image</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => setIsLinkDialogOpen(true)} data-sentry-element="DropdownMenuItem" data-sentry-source-file="index.tsx">
                                        <LinkIcon className="mr-2 h-4 w-4" data-sentry-element="LinkIcon" data-sentry-source-file="index.tsx" />
                                        <span>Insert Link</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="index.tsx" />
                                    <DropdownMenuItem onSelect={handleCopy} data-sentry-element="DropdownMenuItem" data-sentry-source-file="index.tsx">
                                        <Copy className="mr-2 h-4 w-4" data-sentry-element="Copy" data-sentry-source-file="index.tsx" />
                                        <span>Copy to Clipboard</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="index.tsx" />
                                    <DropdownMenuItem onSelect={handleAddTag} data-sentry-element="DropdownMenuItem" data-sentry-source-file="index.tsx">
                                        <Tag className="mr-2 h-4 w-4" data-sentry-element="Tag" data-sentry-source-file="index.tsx" />
                                        <span>Add Tag</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Dialog data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
                                <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="index.tsx">
                                    <StyledButton variant="outline" size="icon" data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                        <Dice5 className="h-4 w-4" data-sentry-element="Dice5" data-sentry-source-file="index.tsx" />
                                    </StyledButton>
                                </DialogTrigger>
                                <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                                    <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="index.tsx">
                                        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="index.tsx">Dice Roller</DialogTitle>
                                        <DialogDescription data-sentry-element="DialogDescription" data-sentry-source-file="index.tsx">
                                            Click the button to roll a d20.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="flex items-center justify-between">
                                        <StyledButton onClick={handleDiceRoll} data-sentry-element="StyledButton" data-sentry-source-file="index.tsx">
                                            Roll d20
                                        </StyledButton>
                                        {diceRoll !== null && <div className="text-2xl font-bold">
                                                {diceRoll}
                                            </div>}
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </TooltipProvider>
                    </Toolbar>
                    {tags.length > 0 && <div className="mb-4 flex flex-wrap gap-2">
                            {tags.map(tag => <TagButton key={tag} size="sm" onClick={() => handleRemoveTag(tag)}>
                                    {tag} ×
                                </TagButton>)}
                        </div>}
                    <div className="mb-4 flex flex-wrap gap-2">
                        {allTags.map(tag => <TagButton key={tag} size="sm" onClick={() => handleTagSuggestionClick(tag)}>
                                {tag}
                            </TagButton>)}
                    </div>
                    <EditorContainer data-sentry-element="EditorContainer" data-sentry-source-file="index.tsx">
                        <EditorContent editor={editor} spellCheck={true} data-sentry-element="EditorContent" data-sentry-source-file="index.tsx" />
                    </EditorContainer>
                    {editor && <BubbleMenu editor={editor} tippyOptions={{
          duration: 100,
          appendTo: () => document.body
        }}>
                            {(["bold", "italic", "underline"] as const).map(type => <BubbleMenuButton key={type} size="sm" onClick={() => toggleCommandMap[type]?.()} active={editor.isActive(type)}>
                                        {type === "bold" && <Bold className="h-4 w-4" />}
                                        {type === "italic" && <Italic className="h-4 w-4" />}
                                        {type === "underline" && <UnderlineIcon className="h-4 w-4" />}
                                    </BubbleMenuButton>)}
                        </BubbleMenu>}
                </CardContent>
                <CardFooter data-sentry-element="CardFooter" data-sentry-source-file="index.tsx">
                    <LastSavedText data-sentry-element="LastSavedText" data-sentry-source-file="index.tsx">
                        {isSaving ? "Saving..." : lastSaved ? `Last saved: ${lastSaved.toLocaleTimeString()}` : "Not saved yet"}
                    </LastSavedText>
                    <div className="flex items-center space-x-4 mt-2">
                        <WordCountText data-sentry-element="WordCountText" data-sentry-source-file="index.tsx">
                            {wordCount} words | {charCount} characters
                        </WordCountText>
                        <SaveButton onClick={handleSave} data-sentry-element="SaveButton" data-sentry-source-file="index.tsx">Save Notes</SaveButton>
                    </div>
                </CardFooter>
            </Card>
            <InsertDialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen} title="Insert Link" placeholder="Enter URL" onSubmit={url => {
      const {
        state
      } = editor;
      const {
        from,
        to
      } = state.selection;
      if (from === to) {
        // No text selected, insert the link text
        editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
      } else {
        // Text is selected, apply the link
        editor.chain().focus().extendMarkRange("link").setLink({
          href: url
        }).run();
      }
    }} data-sentry-element="InsertDialog" data-sentry-source-file="index.tsx" />
            <InsertDialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen} title="Insert Image" placeholder="Enter Image URL" onSubmit={url => {
      editor.chain().focus().setImage({
        src: url
      }).run();
    }} data-sentry-element="InsertDialog" data-sentry-source-file="index.tsx" />
        </>;
}